import { API } from '@/core/Api';

// 이미지를 3D 모델로 변환하는 AI 서비스
export class ImageTo3DAI {
  /**
   * Hunyuan3D Turbo API를 사용하여 이미지를 3D 모델로 변환 (빠른 생성)
   * @param imageUrl 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 3D 모델 정보
   */
  async generateWithHunyuan3DTurbo(
    imageUrl: string,
    options: {
      seed?: number;
      num_inference_steps?: number;
      guidance_scale?: number;
      octree_resolution?: number;
      textured_mesh?: boolean;
      signal?: AbortSignal;
    } = {},
  ): Promise<{
    model: { url: string; file_size: number; file_name: string; content_type: string };
    timings?: any;
  }> {
    const requestData = {
      input_image_url: imageUrl,
      seed: options.seed,
      num_inference_steps: options.num_inference_steps || 50,
      guidance_scale: options.guidance_scale || 7.5,
      octree_resolution: options.octree_resolution || 256,
      textured_mesh: options.textured_mesh || false,
    };

    const response = await API.post<{
      model: { url: string; file_size: number; file_name: string; content_type: string };
      timings?: any;
    }>('/api/ai/image-to-3d/hunyuan3d-turbo', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Hunyuan3D Turbo 3D 모델 생성에 실패했습니다.');
    }
  }

  /**
   * Hyper3D Rodin API를 사용하여 이미지를 3D 모델로 변환 (고품질)
   * @param imageUrl 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 3D 모델 정보
   */
  async generateWithHyper3DRodin(
    imageUrl: string | undefined,
    options: {
      prompt?: string; // 추가 설명 프롬프트
      condition_mode?: 'fuse' | 'concat'; // 이미지 조건 모드
      geometry_file_format?: 'glb' | 'usdz' | 'fbx' | 'obj' | 'stl';
      material?: 'PBR' | 'Shaded';
      quality?: 'high' | 'medium' | 'low' | 'extra-low';
      use_hyper?: boolean; // 하이퍼 모드 (초고품질)
      tier?: 'Regular' | 'Sketch'; // 서비스 티어
      TAPose?: boolean; // T/A 포즈 모드 (휴머노이드용)
      addons?: string; // 추가 기능 (예: "HighPack")
      seed?: number;
      signal?: AbortSignal;
    } = {},
  ): Promise<{
    model: { url: string; file_size: number; file_name: string; content_type: string };
    timings?: any;
  }> {
    const requestData: any = {
      prompt: options.prompt || 'Create a detailed 3D model of this object',
      condition_mode: options.condition_mode || 'fuse',
      geometry_file_format: options.geometry_file_format || 'glb',
      material: options.material || 'PBR',
      quality: options.quality || 'medium',
      use_hyper: options.use_hyper || false,
      tier: options.tier || 'Regular',
      TAPose: options.TAPose || false,
      addons: options.addons,
      seed: options.seed,
    };
    if (imageUrl) {
      requestData['input_image_urls'] = [imageUrl];
    }
    const response = await API.post<{
      model: { url: string; file_size: number; file_name: string; content_type: string };
      timings?: any;
    }>('/api/ai/image-to-3d/hyper3d-rodin', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Hyper3D Rodin 3D 모델 생성에 실패했습니다.');
    }
  }

  /**
   * Hyper3D Rodin을 사용하여 텍스트로 3D 모델 생성
   * @param prompt 3D 모델 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @returns 생성된 3D 모델 정보
   */
  async generateTextTo3DWithRodin(
    prompt: string,
    options: {
      geometry_file_format?: 'glb' | 'usdz' | 'fbx' | 'obj' | 'stl'; // 3D 파일 형식
      material?: 'PBR' | 'Shaded'; // 재질 타입
      quality?: 'high' | 'medium' | 'low' | 'extra-low'; // 품질 설정
      use_hyper?: boolean; // 하이퍼 모드 (초고품질)
      tier?: 'Regular' | 'Sketch'; // 서비스 티어
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      prompt,
      geometry_file_format: options.geometry_file_format || 'glb',
      material: options.material || 'PBR',
      quality: options.quality || 'medium',
      use_hyper: options.use_hyper || false,
      tier: options.tier || 'Regular',
    };

    const response = await API.post('/api/ai/image-to-3d/hyper3d-rodin', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '3D 모델 생성에 실패했습니다.');
    }
  }

  /**
   * Hyper3D Rodin을 사용하여 이미지로 3D 모델 생성
   * @param imageUrls 기반이 될 이미지 URL들
   * @param prompt 추가 설명 프롬프트
   * @param options 추가 옵션들
   * @returns 생성된 3D 모델 정보
   */
  async generateImageTo3DWithRodin(
    imageUrls: string[],
    prompt: string = 'Create a detailed 3D model of this object',
    options: {
      condition_mode?: 'fuse' | 'concat'; // 이미지 조건 모드
      geometry_file_format?: 'glb' | 'usdz' | 'fbx' | 'obj' | 'stl';
      material?: 'PBR' | 'Shaded';
      quality?: 'high' | 'medium' | 'low' | 'extra-low';
      use_hyper?: boolean;
      TAPose?: boolean; // T/A 포즈 모드 (휴머노이드용)
      addons?: string; // 추가 기능 (예: "HighPack")
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      input_image_urls: imageUrls,
      prompt,
      condition_mode: options.condition_mode || 'fuse',
      geometry_file_format: options.geometry_file_format || 'glb',
      material: options.material || 'PBR',
      quality: options.quality || 'medium',
      use_hyper: options.use_hyper || false,
      TAPose: options.TAPose || false,
      addons: options.addons,
    };

    const response = await API.post('/api/ai/image-to-3d/hyper3d-rodin', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지를 3D 모델로 변환하는데 실패했습니다.');
    }
  }

  /**
   * Meshy V5 API를 사용하여 다중 이미지를 3D 모델로 변환
   * @param imageUrls 기반이 될 이미지 URL들 (다중 이미지 지원)
   * @param options 추가 옵션들
   * @returns 생성된 3D 모델 정보
   */
  async generateWithMeshyV5(
    imageUrls: string[],
    options: {
      topology?: 'quad' | 'triangle';
      target_polycount?: number;
      symmetry_mode?: string;
      should_remesh?: boolean;
      should_texture?: boolean;
      enable_safety_checker?: boolean;
      signal?: AbortSignal;
      texture_prompt?: string;
      texture_image_url?: string;
      is_a_t_pose?: boolean;
    } = {},
  ): Promise<{
    model: { url: string; file_size: number; file_name: string; content_type: string };
    timings?: any;
  }> {
    const requestData = {
      image_urls: imageUrls,
      topology: options.topology,
      target_polycount: options.target_polycount,
      symmetry_mode: options.symmetry_mode,
      should_remesh: options.should_remesh,
      should_texture: options.should_texture,
      enable_safety_checker: options.enable_safety_checker,
      texture_prompt: options.texture_prompt,
      texture_image_url: options.texture_image_url,
      is_a_t_pose: options.is_a_t_pose,
    };

    const response = await API.post<{
      model: { url: string; file_size: number; file_name: string; content_type: string };
      timings?: any;
    }>('/api/ai/image-to-3d/meshy-v5', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Meshy V5 3D 모델 생성에 실패했습니다.');
    }
  }

  /**
   * Meshy V6 API를 사용하여 단일 이미지를 3D 모델로 변환
   * @param imageUrl 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 3D 모델 정보
   */
  async generateWithMeshyV6(
    imageUrl: string,
    options: {
      topology?: 'quad' | 'triangle';
      target_polycount?: number;
      symmetry_mode?: string;
      should_remesh?: boolean;
      should_texture?: boolean;
      enable_safety_checker?: boolean;
      signal?: AbortSignal;
      texture_prompt?: string;
      texture_image_url?: string;
      is_a_t_pose?: boolean;
    } = {},
  ): Promise<{
    model: { url: string; file_size: number; file_name: string; content_type: string };
    timings?: any;
  }> {
    const requestData = {
      image_url: imageUrl,
      topology: options.topology,
      target_polycount: options.target_polycount,
      symmetry_mode: options.symmetry_mode,
      should_remesh: options.should_remesh,
      should_texture: options.should_texture,
      enable_safety_checker: options.enable_safety_checker,
      texture_prompt: options.texture_prompt,
      texture_image_url: options.texture_image_url,
      is_a_t_pose: options.is_a_t_pose,
    };

    const response = await API.post<{
      model: { url: string; file_size: number; file_name: string; content_type: string };
      timings?: any;
    }>('/api/ai/image-to-3d/meshy-v6', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Meshy V6 3D 모델 생성에 실패했습니다.');
    }
  }

  /**
   * 다양한 형식으로 3D 모델 생성 테스트
   * @param prompt 텍스트 프롬프트
   * @returns 각 형식별 생성 결과
   */
  async testDifferentFormats(prompt: string) {
    const formats = ['glb', 'usdz', 'fbx', 'obj', 'stl'] as const;
    const results: Record<string, any> = {};

    for (const format of formats) {
      try {
        const result = await this.generateTextTo3DWithRodin(prompt, {
          geometry_file_format: format,
          quality: 'medium',
        });
        results[`format_${format}`] = result;
      } catch (error: any) {
        results[`format_${format}`] = { error: error.message };
      }
    }

    return results;
  }

  /**
   * 다양한 품질 레벨로 3D 모델 생성 테스트
   * @param prompt 텍스트 프롬프트
   * @returns 각 품질별 생성 결과
   */
  async testDifferentQualities(prompt: string) {
    const qualities = ['low', 'medium', 'high'] as const;
    const results: Record<string, any> = {};

    for (const quality of qualities) {
      try {
        const result = await this.generateTextTo3DWithRodin(prompt, {
          quality,
          geometry_file_format: 'glb',
        });
        results[`quality_${quality}`] = result;
      } catch (error: any) {
        results[`quality_${quality}`] = { error: error.message };
      }
    }

    return results;
  }

  /**
   * 사용 가능한 3D 파일 형식들
   */
  getAvailable3DFormats() {
    return [
      { format: 'glb', description: 'GL Transmission Format Binary (웹 최적화)' },
      { format: 'usdz', description: 'Universal Scene Description (AR/iOS)' },
      { format: 'fbx', description: 'Filmbox (게임/애니메이션)' },
      { format: 'obj', description: 'Wavefront OBJ (범용)' },
      { format: 'stl', description: 'Stereolithography (3D 프린팅)' },
    ];
  }

  /**
   * 재질 타입들
   */
  getAvailableMaterials() {
    return [
      { type: 'PBR', description: 'Physically Based Rendering (고품질 재질)' },
      { type: 'Shaded', description: 'Basic Shaded Material (기본 재질)' },
    ];
  }

  /**
   * 품질 레벨들
   */
  getQualityLevels() {
    return [
      { level: 'extra-low', description: '매우 낮음 (빠른 테스트용)' },
      { level: 'low', description: '낮음 (기본 품질)' },
      { level: 'medium', description: '중간 (균형잡힌 품질)' },
      { level: 'high', description: '높음 (고품질, 느림)' },
    ];
  }

  /**
   * 3D 모델 생성용 예제 프롬프트들
   */
  getExample3DPrompts() {
    return [
      'Modern design chair with clean lines',
      'Cute robot character with friendly appearance',
      'Decorative ceramic vase with elegant pattern',
      'Geometric sculpture with abstract patterns',
      'Futuristic sports car with sleek design',
      'Wooden toy house with detailed architecture',
      'Metal helmet with tactical design',
      'Crystal structure with prismatic elements',
    ];
  }

  /**
   * Hunyuan3D 설정 조합들
   */
  getHunyuan3DPresets() {
    return [
      {
        name: '빠른 생성',
        settings: {
          num_inference_steps: 20,
          guidance_scale: 3.5,
          octree_resolution: 128,
          textured_mesh: false,
        },
      },
      {
        name: '표준 품질',
        settings: {
          num_inference_steps: 50,
          guidance_scale: 7.5,
          octree_resolution: 256,
          textured_mesh: false,
        },
      },
      {
        name: '고품질',
        settings: {
          num_inference_steps: 100,
          guidance_scale: 10.0,
          octree_resolution: 512,
          textured_mesh: false,
        },
      },
      {
        name: '텍스처 포함',
        settings: {
          num_inference_steps: 50,
          guidance_scale: 7.5,
          octree_resolution: 256,
          textured_mesh: true,
        },
      },
    ];
  }
}
